import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useSelector } from 'react-redux';

import type { TDisplayOrderQuadrantAbbreviations } from '../modules/patientList/patientStrip/patientStripOdontogram/types';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';

type Dentition = 'primary' | 'permanent';
type QuadNum = '1' | '2' | '3' | '4';

interface ToothLocalization {
  dentition: Dentition;
  fdi: string;
  palmer: string;
  quadAlpha: TDisplayOrderQuadrantAbbreviations;
  quadNum: QuadNum;
  quadNumFdi?: QuadNum;
  universal: string;
}

export interface QuadLocalization {
  quadAlpha: TDisplayOrderQuadrantAbbreviations;
  quadNum: QuadNum;
  quadNumFdi?: QuadNum;
}

interface GetLocalizationResponse {
  toothMapping: ToothLocalization[];
  quadMapping: QuadLocalization[];
}

export const useGetLocalization = () => {
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: any) => state.login.user);

  return useQuery<
    GetLocalizationResponse,
    AxiosError<unknown>,
    GetLocalizationResponse,
    string[]
  >(
    [
      endpoints.localization,
      currentOrganizationId as string,
      currentPracticeId as string,
    ],
    async () => {
      const response = await makeAPIRequestReactQuery<GetLocalizationResponse>(
        endpoints.localization,
        undefined,
        'GET',
        {
          headers: { org: currentOrganizationId, practice: currentPracticeId },
        },
      );
      return response;
    },
    {
      enabled: !!currentOrganizationId,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 10, // 10 minutes
    },
  );
};
